import * as React from 'react'
import { DayPicker } from 'react-day-picker'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'

import 'react-day-picker/style.css'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

/**
 * shadcn-style wrapper around react-day-picker. Theme tokens come from the
 * Tailwind / CSS variables already configured for the rest of the UI so the
 * picker drops into any popover or panel without extra styling.
 *
 * Localized to en-GB so the calendar header reads day-month order, matching
 * the project-wide DD-MM-YYYY date convention.
 */
function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-2', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-2',
        month: 'relative space-y-1',
        month_caption: 'flex justify-center pt-1 items-center h-7',
        caption_label: 'text-sm font-medium',
        nav: 'absolute inset-x-0 top-0 z-20 flex items-center justify-between px-0 pointer-events-none',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'pointer-events-auto relative z-30 h-9 w-9 min-h-9 min-w-9 cursor-pointer bg-transparent p-0 opacity-80 hover:opacity-100 flex items-center justify-center',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'pointer-events-auto relative z-30 h-9 w-9 min-h-9 min-w-9 cursor-pointer bg-transparent p-0 opacity-80 hover:opacity-100 flex items-center justify-center',
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
        week: 'flex w-full mt-1',
        day: 'relative h-8 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20',
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-9 p-0 font-normal aria-selected:opacity-100',
        ),
        range_start: 'day-range-start rounded-l-md bg-primary text-primary-foreground',
        range_end: 'day-range-end rounded-r-md bg-primary text-primary-foreground',
        selected: 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        today: 'bg-accent text-accent-foreground',
        outside: 'day-outside text-muted-foreground opacity-50',
        disabled: 'text-muted-foreground opacity-50',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground rounded-none',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: (chevronProps) =>
          chevronProps.orientation === 'left' ? (
            <ChevronLeft className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          ),
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
